import { BaseService } from "../api/BaseService";
import type { FinalResponse } from "../models";
import type {
  CreateAddressRequest,
  CreateAddressResponse,
  CreateCityRequest,
  CreateCityResponse,
  CreateCountryRequest,
  CreateCountryResponse,
  UpdateCountryRequest,
  UpdateCityRequest,
  UpdateAddressRequest,
  ListCountryResponse,
  ListCityResponse,
  ListAddressResponse,
  AddressFilter,
  SearchCitiesRequest,
  SearchCitiesResponse,
  SearchCountriesRequest,
  SearchCountriesResponse,
} from "../models/address";

export class AddressService extends BaseService {
  private static readonly SERVICE = "Address";

  async createAddress(
    data: CreateAddressRequest,
  ): Promise<CreateAddressResponse> {
    const res = await this.api.request<FinalResponse<CreateAddressResponse>>(
      "/address/address",
      {
        method: "POST",
        body: JSON.stringify(data),
      },
    );
    return this.unwrap(res, AddressService.SERVICE);
  }

  async createCity(data: CreateCityRequest): Promise<CreateCityResponse> {
    const res = await this.api.request<FinalResponse<CreateCityResponse>>(
      "/address/city",
      {
        method: "POST",
        body: JSON.stringify(data),
      },
    );
    return this.unwrap(res, AddressService.SERVICE);
  }

  async createCountry(
    data: CreateCountryRequest,
  ): Promise<CreateCountryResponse> {
    const res = await this.api.request<FinalResponse<CreateCountryResponse>>(
      "/address/country",
      {
        method: "POST",
        body: JSON.stringify(data),
      },
    );
    return this.unwrap(res, AddressService.SERVICE);
  }

  async updateCountry(
    data: UpdateCountryRequest,
  ): Promise<CreateCountryResponse> {
    const res = await this.api.request<FinalResponse<CreateCountryResponse>>(
      `/address/country/${data.id}`,
      {
        method: "PUT",
        body: JSON.stringify(data),
      },
    );
    return this.unwrap(res, AddressService.SERVICE);
  }

  async updateCity(data: UpdateCityRequest): Promise<CreateCityResponse> {
    const res = await this.api.request<FinalResponse<CreateCityResponse>>(
      `/address/city/${data.id}`,
      {
        method: "PUT",
        body: JSON.stringify(data),
      },
    );
    return this.unwrap(res, AddressService.SERVICE);
  }

  async updateAddress(
    data: UpdateAddressRequest,
  ): Promise<CreateAddressResponse> {
    const res = await this.api.request<FinalResponse<CreateAddressResponse>>(
      `/address/address/${data.id}`,
      {
        method: "PUT",
        body: JSON.stringify(data),
      },
    );
    return this.unwrap(res, AddressService.SERVICE);
  }

  async deleteAddress(id: string): Promise<void> {
    const res = await this.api.request<FinalResponse<void>>(
      `/address/address/${id}`,
      {
        method: "DELETE",
      },
    );
    this.unwrap(res, AddressService.SERVICE);
  }

  async deleteCity(id: string): Promise<void> {
    const res = await this.api.request<FinalResponse<void>>(
      `/address/city/${id}`,
      {
        method: "DELETE",
      },
    );
    this.unwrap(res, AddressService.SERVICE);
  }

  async deleteCountry(id: string): Promise<void> {
    const res = await this.api.request<FinalResponse<void>>(
      `/address/country/${id}`,
      {
        method: "DELETE",
      },
    );
    this.unwrap(res, AddressService.SERVICE);
  }

  async fetchCountry(id: string): Promise<CreateCountryResponse> {
    const res = await this.api.request<FinalResponse<CreateCountryResponse>>(
      `/address/country/${id}`,
      {
        method: "GET",
      },
    );
    return this.unwrap(res, AddressService.SERVICE);
  }

  async fetchCity(id: string): Promise<CreateCityResponse> {
    const res = await this.api.request<FinalResponse<CreateCityResponse>>(
      `/address/city/${id}`,
      {
        method: "GET",
      },
    );
    return this.unwrap(res, AddressService.SERVICE);
  }

  async fetchAddress(id: string): Promise<CreateAddressResponse> {
    const res = await this.api.request<FinalResponse<CreateAddressResponse>>(
      `/address/address/${id}`,
      {
        method: "GET",
      },
    );
    return this.unwrap(res, AddressService.SERVICE);
  }

  async fetchListCountry(
    page: number = 1,
    limit: number = 5,
  ): Promise<ListCountryResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    const res = await this.api.request<FinalResponse<ListCountryResponse>>(
      `/address/country?${params}`,
      {
        method: "GET",
      },
    );
    return this.unwrap(res, AddressService.SERVICE);
  }

  async fetchListCity(
    page: number = 1,
    limit: number = 5,
  ): Promise<ListCityResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    const res = await this.api.request<FinalResponse<ListCityResponse>>(
      `/address/city?${params}`,
      {
        method: "GET",
      },
    );
    return this.unwrap(res, AddressService.SERVICE);
  }

  async fetchListAddress(): Promise<ListAddressResponse> {
    const res = await this.api.request<FinalResponse<ListAddressResponse>>(
      "/address/address",
      {
        method: "GET",
      },
    );
    return this.unwrap(res, AddressService.SERVICE);
  }

  async filterAddress(filter: AddressFilter): Promise<ListAddressResponse> {
    const params = new URLSearchParams();
    if (filter.city_uuid) params.append("city_uuid", filter.city_uuid);
    if (filter.country_uuid) params.append("country_uuid", filter.country_uuid);
    if (
      filter.latitude !== undefined &&
      filter.longitude !== undefined &&
      filter.radius !== undefined
    ) {
      params.append("latitude", filter.latitude.toString());
      params.append("longitude", filter.longitude.toString());
      params.append("radius", filter.radius.toString());
    }
    const query = params.toString();
    const url = query ? `/address/address?${query}` : "/address/address";
    const res = await this.api.request<FinalResponse<ListAddressResponse>>(
      url,
      {
        method: "GET",
      },
    );
    return this.unwrap(res, AddressService.SERVICE);
  }

  async searchCities(
    request: SearchCitiesRequest,
  ): Promise<SearchCitiesResponse> {
    const params = new URLSearchParams({ query: request.query });
    if (request.limit !== undefined)
      params.append("limit", request.limit.toString());
    if (request.offset !== undefined)
      params.append("offset", request.offset.toString());
    const res = await this.api.request<FinalResponse<SearchCitiesResponse>>(
      `/address/autocomplete/cities?${params}`,
      {
        method: "GET",
      },
    );
    return this.unwrap(res, AddressService.SERVICE);
  }

  async searchCountries(
    request: SearchCountriesRequest,
  ): Promise<SearchCountriesResponse> {
    const params = new URLSearchParams({ query: request.query });
    if (request.limit !== undefined)
      params.append("limit", request.limit.toString());
    if (request.offset !== undefined)
      params.append("offset", request.offset.toString());
    const res = await this.api.request<FinalResponse<SearchCountriesResponse>>(
      `/address/autocomplete/countries?${params}`,
      {
        method: "GET",
      },
    );
    return this.unwrap(res, AddressService.SERVICE);
  }
}
